import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import GoogleRating from '../GoogleRating';
import { CookieSettingsButton } from '../CookieConsent';

const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/p/Entrep%C3%B4t-Auto-Montr%C3%A9al-61577568119726/',
  linkedin: 'https://www.linkedin.com/company/entrep%C3%B4tautomontreal/'
};

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  return (
    <footer className="bg-slate-900 text-white pb-24 md:pb-0" data-testid="footer" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img 
                src="https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460834/entrepot/assets/company_logo_footer.png" 
                alt="Entrepôt Auto Montréal" 
                className="h-14 w-auto"
              />
              <div>
                <span className="font-heading font-bold text-xl">Entrepôt Auto Montréal</span>
                <p className="text-[10px] text-slate-400 italic">{t('common.tagline')}</p>
              </div>
            </Link>
            <p className="text-slate-400 text-sm">
              {t('footer.description')}
            </p>
            {/* Social Icons */}
            <div className="flex items-center gap-3 mt-4">
              <a 
                href={SOCIAL_LINKS.facebook} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2.5 bg-[#1877F2] text-white hover:bg-[#1877F2]/80 rounded-full transition-colors"
                aria-label="Facebook"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a 
                href={SOCIAL_LINKS.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2.5 bg-[#0A66C2] text-white hover:bg-[#0A66C2]/80 rounded-full transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
            {/* Google Rating */}
            <div className="mt-4">
              <GoogleRating variant="light" />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">{t('footer.quickLinks')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/inventory" className="text-slate-400 hover:text-[#3b82f6] transition-colors text-sm">
                  {t('footer.browseInventory')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-400 hover:text-[#3b82f6] transition-colors text-sm">
                  {t('footer.contactUs')}
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-slate-400 hover:text-[#3b82f6] transition-colors text-sm">
                  {t('footer.adminLogin')}
                </Link>
              </li>
              <li>
                <Link to="/politique-de-confidentialite" className="text-slate-400 hover:text-[#3b82f6] transition-colors text-sm">
                  {t('cookie.privacyLink')}
                </Link>
              </li>
              <li>
                <CookieSettingsButton onClick={() => {
                  // Clear consent to re-show the banner
                  localStorage.removeItem('entrepot_cookie_consent');
                  window.location.reload();
                }} />
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-slate-400">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>1007 boul. René-Lévesque, Verdun (Québec) H3E 1H5</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-400">
                <Phone className="h-4 w-4 flex-shrink-0" />
                <span>(833) 995-6477</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-400">
                <Mail className="h-4 w-4 flex-shrink-0" />
                <span>info@entrepotautomontreal.ca</span>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h3 className="font-heading font-bold text-lg mb-4">{t('footer.businessHours')}</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-3 text-sm text-slate-400">
                <Clock className="h-4 w-4 flex-shrink-0" />
                <div>
                  <p>{t('footer.monThu')}</p>
                  <p>{t('footer.friday')}</p>
                  <p>{t('footer.saturday')}</p>
                  <p>{t('footer.sunday')}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-8 text-center text-sm text-slate-400">
          <p>&copy; {new Date().getFullYear()} Entrepôt Auto Montréal. {t('footer.rights')}</p>
        </div>
      </div>
    </footer>
  );
};
